import type { ReactNode } from "react";
import { AppImage as Image } from "@/components/ui/app-image";
import Link from "@/components/ui/link";
import type { SimpleMediaData } from "@/db/schema/hiive";
import { mediaUrlFromAsset } from "@/lib/media";
import { cn } from "@/lib/utils";

/**
 * The hues a pass is allowed to be.
 *
 * A curated set rather than the whole wheel, because hashing a slug into
 * `0..359` lands a good share of hiives in the 40–70 band, which at pass
 * saturation is mud — "Pilates Academy Bandra" came out the colour of a paper
 * bag. These are picked to hold their character at both the light tint and the
 * dark one.
 */
const PASS_HUES = [265, 224, 196, 168, 142, 14, 330, 292] as const;

/**
 * A stable hue per issuing hiive.
 *
 * Identity is carried by colour so a wallet holding four passes reads as four
 * issuers before a single word is read. Derived from the slug rather than
 * stored, because it must never drift and there is nothing to migrate.
 */
export function hueForHiive(slug: string): number {
  let hash = 0;
  for (let index = 0; index < slug.length; index += 1) {
    hash = (hash * 31 + slug.charCodeAt(index)) % 9973;
  }
  return PASS_HUES[hash % PASS_HUES.length];
}

/**
 * The ticket shape a pack wears, wherever it appears.
 *
 * Shared between the wallet and the shop on purpose: the thing you buy and the
 * thing you then hold are the same object, and drawing the purchase as a
 * settings row and the holding as a ticket made them look like two features.
 *
 * The notch is punched with a mask rather than drawn with two
 * background-coloured circles, because these sit on gradients and matching
 * circles give themselves away at every scroll position. It is measured from
 * the *bottom* edge, because the stub is fixed-height while everything above it
 * grows with the pack's name.
 */
export function PackPassShell({
  hiiveSlug,
  dimmed = false,
  href,
  children,
  className,
}: {
  hiiveSlug: string;
  dimmed?: boolean;
  /** Renders the pass as a link, for a pass you can still buy. */
  href?: string;
  children: ReactNode;
  className?: string;
}) {
  const body = (
    <div className="pack-pass-face relative ring-1 ring-black/[0.06] ring-inset dark:ring-white/[0.07]">
      {/* A single soft light source, so the face reads as a surface rather than
          a flat fill. Behind everything and never interactive. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_12%_-10%,rgba(255,255,255,0.55),transparent_58%)] dark:bg-[radial-gradient(circle_at_12%_-10%,rgba(255,255,255,0.12),transparent_58%)]"
      />
      <div className="relative z-10">{children}</div>
    </div>
  );

  const shell = cn(
    "pack-pass relative block overflow-hidden rounded-[1.6rem]",
    // A spent pass stays legible — somebody needs to see that the pack they
    // remember buying is used up, not wonder where it went — but it stops
    // competing with the ones that still work.
    dimmed && "opacity-55 saturate-[0.35]",
    className,
  );

  const style = { "--pass-hue": hueForHiive(hiiveSlug) } as React.CSSProperties;

  if (href) {
    return (
      <Link
        href={href}
        style={style}
        className={cn(
          shell,
          "transition-transform duration-200 ease-[var(--ease-out-strong)] active:scale-[0.985]",
          "[@media(hover:hover)]:hover:-translate-y-0.5",
        )}
      >
        {body}
      </Link>
    );
  }

  return (
    <article style={style} className={shell}>
      {body}
    </article>
  );
}

/** Who issued this pass, and one optional status chip. */
export function PackPassHeader({
  hiiveName,
  logoImage,
  chip,
}: {
  hiiveName: string;
  logoImage: SimpleMediaData | null;
  chip?: ReactNode;
}) {
  const logoUrl = mediaUrlFromAsset(logoImage);
  return (
    <div className="flex items-center gap-2.5 px-5 pt-4">
      <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
        {logoUrl ? (
          <Image
            src={logoUrl}
            alt=""
            width={24}
            height={24}
            className="size-full object-cover"
          />
        ) : (
          <span className="text-[11px] font-semibold text-text-secondary">
            {hiiveName.charAt(0).toUpperCase()}
          </span>
        )}
      </span>
      <p className="min-w-0 flex-1 truncate text-[12.5px] font-medium tracking-wider text-text-secondary uppercase">
        {hiiveName}
      </p>
      {chip}
    </div>
  );
}

export function PackPassTitle({ children }: { children: ReactNode }) {
  return (
    <h3 className="mt-1.5 px-5 text-[1.35rem] leading-tight font-semibold tracking-tight text-text-primary">
      {children}
    </h3>
  );
}

/**
 * The tear-off below the perforation.
 *
 * Height is pinned to `--pass-stub-h` because the punched notch is measured
 * from the bottom edge; a stub that grew would slide off its own tear line.
 */
export function PackPassStub({
  label,
  value,
  /**
   * Drop the small-caps label treatment.
   *
   * The wallet's stub leads with a label ("VALID UNTIL") and the shop's leads
   * with a price. Letter-spaced 10px caps is right for the first and wrong for
   * the second — a price is the number somebody is deciding on.
   */
  plainLabel = false,
}: {
  label: ReactNode;
  value: ReactNode;
  plainLabel?: boolean;
}) {
  return (
    <>
      <div
        aria-hidden="true"
        className="mt-5 border-t border-dashed border-black/[0.14] dark:border-white/[0.18]"
      />
      <div className="flex h-[var(--pass-stub-h)] items-center justify-between gap-3 bg-black/[0.035] px-5 dark:bg-black/20">
        <span
          className={cn(
            "shrink-0",
            plainLabel
              ? "text-[1.05rem] font-semibold text-text-primary tabular-nums"
              : "text-[10.5px] font-medium tracking-[0.12em] text-text-tertiary uppercase",
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            "truncate text-right",
            plainLabel
              ? "text-[12px] text-text-secondary"
              : "text-[13px] font-medium text-text-primary",
          )}
        >
          {value}
        </span>
      </div>
    </>
  );
}

export function PackPassChip({
  tone,
  children,
}: {
  tone: "amber" | "muted" | "brand";
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase",
        tone === "amber" &&
          "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        tone === "muted" &&
          "bg-black/[0.07] text-text-tertiary dark:bg-white/[0.09]",
        tone === "brand" &&
          "bg-success/15 text-success dark:bg-[#F5E6B8]/15 dark:text-[#F5E6B8]",
      )}
    >
      {children}
    </span>
  );
}
